"use client";

import React, { ReactNode, Suspense, useState, useEffect } from "react";
import {
  getCardImage,
  getCardData,
} from "@/lib/games/onePiece/getCardDataLocal";
import getCardRules from "@/lib/getCardRules";
import { getTcgPlayerIdAA } from "@/lib/tcgplayer/getCardPrice";
import { replaceKeywords } from "@/components/keywords";
import { RulesQA } from "@/components/CardPage/RulesQA";
import { Card, Image, Spinner } from "@heroui/react";
import AltArts, { getAACard } from "@/components/CardPage/AltArts";
import { AACard } from "@/models/alt-arts";
import dynamic from "next/dynamic";

const PriceHistoryChartDynamic = dynamic(
  () => import("../PriceHistory/PriceHistoryChartNoSSR"),
  {
    ssr: false,
    loading: () => <Spinner />,
  },
);

function getSetFromNumber(number: string): string {
  let [first, ...rest] = number.split("-");
  return first;
}

function formatName(name: string, cardType: string): string {
  // Remove ending # if it exists e.g. Roronoa Zoro (001)

  // TODO: Check if there is Parallel, AA, SP, etc in name and add that on
  let newName = name.replace("Alternate Art", "AA").replace("(", ",(");
  let [first, ...rest] = newName.split(",");

  let displayName;

  if (cardType == "Leader") {
    displayName = first + " Leader";
  } else {
    displayName = first;
  }

  if (rest.length > 0) {
    displayName = displayName + " " + rest.join("");
  }
  return displayName;
}

function formatCardInfo(card: any): ReactNode {
  const fields = [
    "name",
    "number",
    "rarity",
    "type",
    "color",
    "life",
    "cost",
    "power",
    "counter",
    "subtype",
    "attribute",
  ];
  let data = [];

  for (const field of fields) {
    let displayField = capitalize(field);
    if (field === "subtype") {
      displayField = "Subtype(s)";
    }
    if (!(field in card) || card[field] === "-") {
      continue;
    }
    const row = (
      <React.Fragment key={field}>
        <div className="h-[24px] text-sm">{displayField}</div>
        <div className="h-[24px] text-sm">{card[field]}</div>
      </React.Fragment>
    );
    data.push(row);
  }

  return <div className="grid grid-cols-[86px_1fr] text-base">{data}</div>;
}

function onlyName(name: string): string {
  let [first, ...rest] = name.split("(");
  return first;
}

function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

type RuleEntry = {
  question: string;
  answer: string;
};

type Rules = {
  [cardNumber: string]: {
    rules: RuleEntry[];
  };
};

export default function CardPageComponent({
  cardNumber,
  src,
  id,
}: {
  cardNumber: string;
  src?: string;
  id?: string;
}) {
  // const [src, setSrc] = useQueryState("src", parseAsString.withDefault(""));
  // const [id, setId] = useQueryState("id", parseAsString.withDefault(""));
  const [selectedArt, setSelectedArt] = useState<AACard>(
    getAACard(cardNumber, src, id),
  );
  const [tcgplayerId, setTcgPlayerId] = useState<string | undefined>(
    getTcgPlayerIdAA(selectedArt),
  );
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState<Rules>({});
  let groupId = getSetFromNumber(cardNumber);

  useEffect(() => {
    async function fetchData() {
      try {
        const [rules] = await Promise.all([getCardRules(groupId)]);
        setRules(rules);
      } catch (error) {
        console.error("Failed to fetch rules:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [cardNumber, groupId]);

  function handleAAClick(card: AACard) {
    // setSrc(card.src);
    // setId(card.id);
    setSelectedArt(card);
    setTcgPlayerId(getTcgPlayerIdAA(card));
  }

  const card = getCardData(cardNumber);

  const data = card;
  if (!data) return <p>No market data available</p>;
  const name = data.name;
  const cardType = data.type;
  const number = data.number;
  const cost = data.cost;
  const newImgUrl = getCardImage(selectedArt.id, "original", selectedArt.src);

  let trigger;
  if (data.trigger) {
    trigger = replaceKeywords(data.trigger);
  }

  let rulesDisplay = undefined;
  if (number in rules) {
    // console.log("rules", rules);
    let newRules = rules[number]["rules"];
    rulesDisplay = <RulesQA name={name} rules={newRules} groupId={groupId} />;
  } else {
    rulesDisplay = undefined;
  }

  return (
    <div className="font-extralight">
      <div>
        <h1 className="inline-block text-2xl sm:text-4xl font-semibold text-slate-900 tracking-tight dark:text-slate-100">
          <span className="text-slate-500">
            {getSetFromNumber(number)}{" "}
            {cost !== undefined && cost != null && cost + "c"}
          </span>{" "}
          {formatName(name, cardType)}
        </h1>
        <h2 className="mt-2 text-slate-700 dark:text-slate-300 text-lg">
          The OPTCG {number} {cost != undefined && cost !== null && cost + "c"}{" "}
          {data.color} {onlyName(name)} {data.type} card with English card
          effect.
        </h2>
        <div className="md:flex md:flex-wrap mt-4">
          <div className="mb-4 flex items-center justify-center md:mb-0 md:w-2/5 md:pr-4">
            <Image src={newImgUrl} alt={name} width={357} height={500} />
          </div>
          <div className="md:w-3/5 items-stretch flex">
            <Card
              className="leading-loose pt-4 px-4 md:px-6 pb-6 dark:text-slate-300 w-full"
              shadow="sm"
            >
              <div className="flex flex-col justify-around h-full">
                <div>
                  <h2 className="font-normal text-xl dark:text-slate-200 mb-2">
                    Card Effect
                  </h2>
                  <div id="card-effect" className="inline-block">
                    {replaceKeywords(data.description)}
                  </div>
                  <div className="inline-block">{trigger}</div>
                </div>
                <div>
                  <h2 className="font-normal text-xl dark:text-slate-200 mb-2 mt-4 sm:mt-0">
                    Card Info
                  </h2>
                  {formatCardInfo(data)}
                </div>
              </div>
            </Card>
          </div>
          <div className="my-4 w-full">
            <div className="text-xl font-semibold mb-2">Alternate Arts</div>
            <AltArts
              selected={selectedArt}
              number={number}
              handleClick={handleAAClick}
            />
          </div>
          {card && (
            <div className="md:w-2/5 md:pr-4 mt-4">
              <Card className="p-4">
                <div className="min-h-[388px] flex items-center justify-center">
                  <Suspense
                    fallback={
                      <div>
                        <Spinner />
                      </div>
                    }
                  >
                    {tcgplayerId && (
                      <PriceHistoryChartDynamic
                        key={tcgplayerId}
                        tcgplayerId={tcgplayerId}
                        game={"one-piece"}
                      />
                    )}
                    {!tcgplayerId && <div>No market data available</div>}
                  </Suspense>
                </div>
              </Card>
            </div>
          )}
          {rulesDisplay && (
            <div className="md:w-3/5">
              <div>{rulesDisplay}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
